/**
 * Canonical phone handling, shared by the CRM entry forms, the tour-operator
 * assignment guard and the WhatsApp send boundary. One definition so entry
 * validation and send validation cannot drift apart.
 */

/**
 * Strip a pasted phone down to `+`-and-digits. Handles the shapes humans
 * actually paste: spaces, non-breaking spaces, punctuation, and the bidi
 * controls (U+202D…U+202C) that come with a copied contact card.
 *
 *   1. drop Unicode format/control characters (Cf/Cc);
 *   2. drop everything that is not a digit or `+`;
 *   3. collapse to at most ONE leading `+`.
 */
export const normalizeRecipientPhone = (recipient: string): string => {
  const stripped = recipient
    .replace(/[\p{Cf}\p{Cc}]/gu, '')
    .replace(/[^\d+]/gu, '');
  const digits = stripped.replace(/\+/g, '');
  if (digits.length === 0) return '';
  return stripped.startsWith('+') ? `+${digits}` : digits;
};

/** E.164: a leading `+`, a non-zero country digit, then 7–15 digits total. */
const E164_RE = /^\+[1-9]\d{6,14}$/;

/**
 * Deliberately does NOT rewrite an international-prefix form like
 * `0034624007913` to `+34…`, nor infer a country for a bare national number.
 * Inferring a country is a business rule nobody has stated, and guessing it
 * wrong sends a real message to the wrong person.
 */
export const isCanonicalRecipientPhone = (value: string): boolean =>
  E164_RE.test(value);

/**
 * Normalise a phone for STORAGE: returns the canonical `+<country><digits>`
 * form, or `null` when the input is blank. Unlike the send-boundary use, this
 * result is written back to the row, so whitespace never reaches the database.
 * Returns the normalised value even when it is not valid E.164 — callers pair
 * this with `isCanonicalRecipientPhone` to reject.
 */
export const normalizePhoneForStorage = (
  value: string | null | undefined
): string | null => {
  if (value == null) return null;
  const normalized = normalizeRecipientPhone(value);
  return normalized === '' ? null : normalized;
};
